import { Grid, Skeleton, Stack, TableCell, Typography } from "@mui/material";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient } from "react-query";
import { generateColorGradient, handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import {
  activeWorkshopsPerMonth,
  mandayPerMonthAndYear,
  mandayPerVenue,
  topNSubjects,
  trainerWorkshopByStatus,
  trainerWorkshopByTopic,
  workshopsAttendance,
  workshopsPerProvider,
  workshopsStatusCount,
} from "framework/charts";
import { IChartView, IFormField, IOption } from "models/app";
import { CHARTS, InputTypes } from "constants/enums";
import ChartView from "components/UI/ChartView/ChartView";
import { TrainerWorkshopChartsCells, WorkshopChartsCells } from "framework/workshops";
import { IWorkshop } from "models/workshop";
import Filters from "./partials/filters";
import StackedBarChart from "./partials/stacked";
import { ChartData } from "chart.js";
import FormFields from "components/UI/FormFields";
import { useForm } from "react-hook-form";
import { getPermission, getUserData } from "helpers";
import { IPermission } from "models/permissions";
import EmptyContent from "components/EmptyContent";
import BlockIcon from "@mui/icons-material/Block";
import { Bar } from "react-chartjs-2";
interface Props {
  feature: Feature;
}

const TrainerAnalytics: React.FC<Props> = ({ feature }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [loading3, setLoading3] = useState<boolean>(true);
  const [permission, setPermission] = useState<IPermission | null>(null);
  const { t, i18n } = useTranslation();
  const [yearStr, setYear] = useState<string[]>([
    new Date().getFullYear().toString(),
  ]);

  const [data1, setData1] = useState<any[]>([]);
  const [data2, setData2] = useState<any[]>([]);
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const [dateFrom, setDateFrom] = useState<any>(new Date(year, month, 1));
  const [dateTo, setDateTo] = useState<any>(new Date(year, month + 1, 0));
  const user = getUserData();
  const { colors: colors1 } = user;

  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {


      let promise1 = queryClient.fetchQuery(
        "get Trainer Workshops BY Topic",
        () =>
          trainerWorkshopByTopic(
            `?fromDate=${dateFrom!.toISOString()}&toDate=${dateTo!.toISOString()}`
          )
      );

      let promise2 = queryClient.fetchQuery(
        "get Trainer Workshops BY Status",
        () =>
          trainerWorkshopByStatus(
            `?fromDate=${dateFrom!.toISOString()}&toDate=${dateTo!.toISOString()}`
          )
      );


      const [
        response1,
        response2
      ] = await Promise.all([
        promise1,
        promise2,
      ]);
      setData1(response1);
      setData2(response2);

      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchPermission = useCallback(async () => {
    setLoading3(true);
    try {
      const res = await getPermission(21);
      setPermission(res);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);


  useEffect(() => {
    fetchData();
    fetchPermission();

    // eslint-disable-next-line
  }, [dateFrom, dateTo]);



  useEffect(() => {
    fetchPermission();

    // eslint-disable-next-line
  }, []);
  const getBodyCells = (row: any) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "workshopStatus")
        return {
          id: "workshopStatus",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                { row.workshopStatus }
              </Typography>
            </TableCell>
          ),
        };

      if (key === "status")
        return {
          id: "status",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.status}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "topicName")
        return {
          id: "topicName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row.topicName : row.topicNameAr}
              </Typography>
            </TableCell>
          ),
        };
   

      if (key === "fromDate")
        return {
          id: "fromDate",
          element: (
            <TableCell align={"left"} key={i}>
              {row.fromDate && (
                <Typography variant="body2">
                  {row.fromDate}
                </Typography>
              )}
            </TableCell>
          ),
        };
      if (key === "toDate")
        return {
          id: "toDate",
          element: (
            <TableCell align={"left"} key={i}>
              {row.toDate && (
                <Typography variant="body2">
                  {row.toDate}
                </Typography>
              )}
            </TableCell>
          ),
        };
      else return <></>;
    });

    return cells;
  };



  if (loading3)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  if (!permission?.hasReadPermission)
    return (
      <EmptyContent
        icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
        header={t("T_no_permissions")}
        message={t("T_no_permissions_message")}
      />
    );


    const chartData1: IChartView = {
      data: {
        Labels: data1.map((item: any) => item?.name),
        Values: data1.map((item: any) => item?.count),
      },
      label: t("T_trainer_dashboard_2"),
      type: CHARTS.BAR,
      backgroundColor: colors1?.chartMainColor || "#513cc7",
  
      borderColor: "#513cc7",
  
      Id: "mandayPerVendor",
    };

    
    const topics = [...new Set(data2.map((item) => item?.topicName))];
    const workshops = [...new Set(data2.map((item) => item?.status))];
    
    // Count occurrences of each topic grouped by workshopStatus
    const topicWorkshopCounts = topics.reduce((acc, topic) => {
      acc[topic] = workshops.reduce((statusAcc, workshop) => {
        statusAcc[workshop] = data2.filter(
          (item) => item.topicName === topic && item.status === workshop
        ).length;
        return statusAcc;
      }, {});
      return acc;
    }, {});
    
    // Prepare chartData with correct structure
    const chartData = {
      labels: topics,
      datasets: workshops.map((workshop, index) => ({
        label: workshop,
        data: topics.map((topic) => topicWorkshopCounts[topic][workshop]),
        backgroundColor: generateColorGradient(
          colors1?.chartMainColor,
          workshops.length,
          2,
          2
        )[index],
      })),
    };
    
    

    const options1: any = {
      indexAxis: "y" as const,
      responsive: true,
      plugins: {
        legend: {
          position: "top" as const,
          labels: {
            font: {
              size: 17,
              weight: "bold",
            },
          },
        },
        datalabels: {
          display: true,
          color: "white",
          font: {
            size: 20,
          },
        },
      },
  
      scales: {
        x: {
          stacked: true,
          ticks: {
            font: {
              size: 14,
              weight: "bold",
            },
          },
        },
        y: {
          stacked: true,
          ticks: {
            font: {
              size: 14,
              weight: "bold",
            },
          },
        },
      },
      elements: {
        bar: {
          borderRadius: 10,
        },
      },
    };

  return (
    <Stack spacing={2} p={2}>
 

      <Grid
        container
        justifyContent={"space-between"}
        alignItems="center"
        
      >
        <Stack direction={"row"} spacing={1} sx={{ mb: 4 }}>
          <Filters
            onChangeFrom={(e) => setDateFrom(e)}
            onChangeTo={(e) => setDateTo(e)}
            valueFrom={dateFrom}
            valueTo={dateTo}
          />
        </Stack>
      </Grid>
      {loading ? (
        <Stack spacing={2} p={2}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton variant="rectangular" key={i} width="100%" height={30} />
          ))}
        </Stack>
      ) : (
        <div>
          <Typography
            variant="h4"
            sx={{
              alignItems: "center",
            }}
          >
            {t("T_trainer_dashboard_1")}
          </Typography>
          <List
            headCells={TrainerWorkshopChartsCells}
            data={data2}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        </div>
      )}



            <Grid container item xs={12} sm={6}>
              <Typography
                variant="h4"
                sx={{
                  alignItems: "center",
                }}
              >
                {t("T_trainer_dashboard_2")}
              </Typography>
              <Bar options={options1} data={chartData} />
            </Grid>
    </Stack>
  );
};

export default TrainerAnalytics;
